import React from 'react';
import { Auth } from 'aws-amplify';
import Logo from '../img/almabetter-logo-new.png';
import LogoutIcon from '../icons/LogoutIcon';
import { Link, useRouteMatch, useLocation } from 'react-router-dom';
import DashboardIcon from '../icons/DashboardIcon';
import SessionContollerIcon from '../icons/SessionControllerIcon';
import UsersIcon from '../icons/UsersIcon';
import ReferEarnIcon from '../icons/ReferEarnIcon';
import AdminIcon from '../icons/AdminIcon';
import ManageBookingsIcon from '../icons/ManageBookingsIcon';
import { useDispatch, useSelector } from 'react-redux';
import { setLoggedIn } from '../redux/reducers/userReducer';
import { MODULE_NAMES } from '../constants';
import { useTheme } from '../theme/useTheme';

const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 240;

export default function SideBar() {
  const modules = useSelector((state) => state.userReducer.modules);
  const { toggleTheme, isDarkMode } = useTheme();
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [salesCollapsible, setSalesCollapsible] = React.useState(true);
  const [coachOperationsCollapsible, setCoachOperationsCollapsible] = React.useState(true);
  const [learningTechCollapsible, setLearningTechCollapsible] = React.useState(true);
  const [studentOperationsCollapsible, setStudentOperationsCollapsible] = React.useState(true);
  const [scrapedJobsCollapsible, setScrappedJobsCollapsible] = React.useState(true);
  const [jobDashboardV2Collapse, setJobDashboardV2Collapse] = React.useState(true);
  const [sessionCollapsible, setSessionCollapsible] = React.useState(true);
  const [almaXCollapsible, setAlmaXCollapsible] = React.useState(true);

  const dispatch = useDispatch();


  const logout = async () => {
    try {
      await Auth.signOut();
      dispatch(setLoggedIn(false));
    } catch (error) {
      console.error('error signing out: ', error);
    }
  };

  const hasModuleAccess = (moduleName) =>
    modules.includes('super-admin') || modules.includes(moduleName);

  const location = useLocation();

  const isSectionActive = (paths) => paths.some(p => location.pathname.startsWith(p));

  const handleMenuClick = (setSectionState, sectionState) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      return;
    }
    setSectionState(!sectionState);
  };

  const MenuItem = ({ icon, label, onClick, showChevron, chevronCollapsed, moduleAccess = true, isActive = false }) => {
    if (!moduleAccess) return null;
    return (
      <div
        onClick={onClick}
        title={isCollapsed ? label : ''}
        style={{
          padding: isCollapsed ? '8px 0' : '8px 12px',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          borderRadius: '6px',
          marginBottom: '2px',
          backgroundColor: isActive ? 'var(--sidebar-active-bg, #EEF2FF)' : 'transparent',
          transition: 'background-color 150ms ease',
        }}
        className={`cursor-pointer flex items-center text-[13px] transition-all duration-150 hover:bg-[var(--sidebar-hover-bg, #F7F7F9)] ${isActive ? 'text-[var(--sidebar-active, #4F46E5)]' : ''}`}
      >
        <span style={{ flexShrink: 0, minWidth: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
        <span
          style={{
            margin: isCollapsed ? '0' : 'auto 0 auto 12px',
            flexGrow: isCollapsed ? 0 : 1,
            whiteSpace: 'nowrap',
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : 'auto',
            transition: 'opacity 200ms, width 200ms, margin 200ms',
            overflow: 'hidden',
            fontSize: '13px',
            fontWeight: isActive ? 600 : 500,
            color: isActive ? 'var(--sidebar-active, #4F46E5)' : 'var(--sidebar-text, #374151)',
            lineHeight: 1.4,
          }}
        >
          {label}
        </span>
        {showChevron && (
          <svg
            style={{
              flexShrink: 0,
              transition: 'transform 150ms ease, opacity 200ms',
              transform: chevronCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              opacity: isCollapsed ? 0 : 1,
            }}
            width="12" height="12" viewBox="0 0 24 24"
            fill="none" stroke="#C4C9D4" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        )}
      </div>
    );
  };

  const SubMenu = ({ children, hidden, isNested = false }) => {
    if (isCollapsed || hidden) return null;
    return (
      <div
        style={{
          position: 'relative',
          borderLeft: isNested ? '1.5px dashed var(--border-light, #E5E7EB)' : '1.5px solid var(--border-light, #E5E7EB)',
          margin: isNested ? '2px 0 4px 20px' : '4px 0 8px 27px',
          paddingLeft: '0',
        }}
      >
        {React.Children.map(children, child =>
          React.isValidElement(child) ? React.cloneElement(child, { isNested, showTick: true }) : child
        )}
      </div>
    );
  };
  function ThemeSwitcher({ isDarkMode, toggleTheme, isCollapsed }) {
    return (
      <div
        role="radiogroup"
        style={{
          display: 'flex',
          width: isCollapsed ? 0 : 'fit-content',
          padding: '2px',
          borderRadius: '9999px',
          border: '1px solid var(--border-main, #D1D5DB)',
          opacity: isCollapsed ? 0 : 1,
          overflow: 'hidden',
          transition: 'opacity 200ms, width 200ms',
        }}
      >
        <button
          type="button"
          role="radio"
          aria-checked={!isDarkMode}
          aria-label="Switch to light theme"
          title="Light theme"
          onClick={() => isDarkMode && toggleTheme()}
          style={{
            width: '26px',
            height: '26px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 0,
            borderRadius: '9999px',
            cursor: 'pointer',
            background: !isDarkMode ? 'var(--sidebar-active-bg, rgba(79,70,229,0.1))' : 'none',
            color: !isDarkMode ? 'var(--sidebar-active, #4F46E5)' : 'var(--sidebar-text-muted, #9CA3AF)',
            transition: 'color 150ms, background-color 150ms',
          }}
          onMouseEnter={(e) => { if (isDarkMode) e.currentTarget.style.color = '#374151'; }}
          onMouseLeave={(e) => { if (isDarkMode) e.currentTarget.style.color = 'var(--sidebar-text-muted, #9CA3AF)'; }}
          onMouseDown={(e) => { e.currentTarget.style.background = 'rgba(128,128,128,0.35)'; e.currentTarget.style.color = '#fff'; }}
          onMouseUp={(e) => { e.currentTarget.style.background = !isDarkMode ? 'var(--sidebar-active-bg, rgba(79,70,229,0.1))' : 'none'; }}
        >
          <span style={{ width: '14px', height: '14px', display: 'flex' }}>
            <SunIcon />
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={isDarkMode}
          aria-label="Switch to dark theme"
          title="Dark theme"
          onClick={() => !isDarkMode && toggleTheme()}
          style={{
            width: '26px',
            height: '26px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 0,
            borderRadius: '9999px',
            cursor: 'pointer',
            background: isDarkMode ? 'var(--sidebar-active-bg, rgba(79,70,229,0.1))' : 'none',
            color: isDarkMode ? 'var(--sidebar-active, #4F46E5)' : 'var(--sidebar-text-muted, #9CA3AF)',
            transition: 'color 150ms, background-color 150ms',
          }}
          onMouseEnter={(e) => { if (!isDarkMode) e.currentTarget.style.color = '#374151'; }}
          onMouseLeave={(e) => { if (!isDarkMode) e.currentTarget.style.color = 'var(--sidebar-text-muted, #9CA3AF)'; }}
          onMouseDown={(e) => { e.currentTarget.style.background = 'rgba(128,128,128,0.35)'; e.currentTarget.style.color = '#fff'; }}
          onMouseUp={(e) => { e.currentTarget.style.background = isDarkMode ? 'var(--sidebar-active-bg, rgba(79,70,229,0.1))' : 'none'; }}
        >
          <span style={{ width: '14px', height: '14px', display: 'flex' }}>
            <MoonIcon />
          </span>
        </button>
      </div>
    );
  }

const iconProps = {
  width: 14, height: 14, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round',
};

const SunIcon = () => (
  <svg {...iconProps}>
    <circle r="5" cy="12" cx="12" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

const MoonIcon = () => (
  <svg {...iconProps}>
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
  </svg>
);

  return (
    <div
      style={{
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        width: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), flex-basis 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: 'var(--sidebar-background)',
      }}
      className="h-screen sticky top-0 overflow-y-auto overflow-x-hidden scrollbar-hide flex flex-col"
    >
      {/* Header */}
      <div
        className="flex items-center transition-colors duration-300"
        style={{
          padding: isCollapsed ? '16px 8px' : '16px',
          borderBottom: '1px solid',
          borderColor: 'var(--border-main)',
        }}
      >
        <img
          src={Logo}
          alt="AlmaBetter Logo"
          style={{
            height: '32px',
            whiteSpace: 'nowrap',
            transition: 'opacity 200ms, width 200ms',
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : 'auto',
            overflow: 'hidden',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto',
          }}
        >
          {/* Collapse Toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              padding: 0,
              borderRadius: '8px',
              color: 'var(--sidebar-text)',
              background: 'transparent',
              border: 'none',
              transition: 'all 200ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--sidebar-hover-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <svg
              style={{
                transition: 'transform 200ms',
                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              }}
              width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          {/* Theme Toggle */}
          <ThemeSwitcher isDarkMode={isDarkMode} toggleTheme={toggleTheme} isCollapsed={isCollapsed} />
        </div>
      </div>

      {/* Menu Options */}
      <div
        style={{
          marginTop: '8px',
          fontWeight: 500,
          letterSpacing: '0.025em',
          width: '100%',
          color: 'var(--sidebar-text)',
          padding: '0 6px',
        }}
      >
        {/* Sales & Marketing */}
        <MenuItem
          icon={<ManageBookingsIcon iconStyle="w-5 h-5" />}
          label="Sales & Marketting"
          moduleAccess={hasModuleAccess(MODULE_NAMES.SALES_MARKETING)}
          onClick={() => handleMenuClick(setSalesCollapsible, salesCollapsible)}
          showChevron={!isCollapsed}
          chevronCollapsed={salesCollapsible}
          isActive={isSectionActive(['/payment-links', '/coupons', '/refer-earn', '/almabetter-website'])}
        />
        <SubMenu hidden={salesCollapsible}>
          <SideBarLink to="/payment-links" title="Payment Links" isCollapsed={isCollapsed} />
          <SideBarLink to="/coupons" title="Coupons" isCollapsed={isCollapsed} />
          <SideBarLink to="/refer-earn" title="Refer and Earn" isCollapsed={isCollapsed} />
          <SideBarLink to="/almabetter-website" title="AlmaBetter Website" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* Student Operations */}
        <MenuItem
          icon={<UsersIcon iconStyle="w-5 h-5" />}
          label="Student Operations"
          moduleAccess={hasModuleAccess('student-operations')}
          onClick={() => handleMenuClick(setStudentOperationsCollapsible, studentOperationsCollapsible)}
          showChevron={!isCollapsed}
          chevronCollapsed={studentOperationsCollapsible}
          isActive={isSectionActive(['/all-users', '/student-groups'])}
        />
        <SubMenu hidden={studentOperationsCollapsible}>
          <SideBarLink to="/all-users" title="Free Users" isCollapsed={isCollapsed} exact />
          <SideBarLink to="/all-users/enrolled" title="Enrolled Users" isCollapsed={isCollapsed} />
          <SideBarLink to="/all-users/enrolment-financial" title="Enrollment Financial Details" isCollapsed={isCollapsed} />
          <SideBarLink to="/student-groups" title="Student Groups" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* Coach Operations */}
        <MenuItem
          icon={<UsersIcon iconStyle="w-5 h-5" />}
          label="Coach Operations"
          moduleAccess={hasModuleAccess('coach-operations')}
          onClick={() => handleMenuClick(setCoachOperationsCollapsible, coachOperationsCollapsible)}
          showChevron={!isCollapsed}
          chevronCollapsed={coachOperationsCollapsible}
          isActive={isSectionActive(['/coach', '/coach-verification', '/kyc-verifications', '/expertise-verification'])}
        />
        <SubMenu hidden={coachOperationsCollapsible}>
          <SideBarLink to="/coach" title="Coaches" isCollapsed={isCollapsed} />
          <SideBarLink to="/coach-verification" title="Coach Verification" isCollapsed={isCollapsed} />
          <SideBarLink to="/kyc-verifications" title="KYC Verifications" isCollapsed={isCollapsed} />
          <SideBarLink to="/expertise-verification" title="Expertise Verification" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* Learning Ops */}
        <MenuItem
          icon={<ReferEarnIcon iconStyle="w-5 h-5" />}
          label="Learning Ops"
          moduleAccess={hasModuleAccess('learning-tech')}
          onClick={() => handleMenuClick(setLearningTechCollapsible, learningTechCollapsible)}
          showChevron={!isCollapsed}
          chevronCollapsed={learningTechCollapsible}
          isActive={isSectionActive(['/manage-bookings', '/manage-companies', '/projects', '/almax-submissions', '/manage-classroom', '/manage-abcat-extension', '/progress-call', '/cohorts', '/support-settings'])}
        />
        <SubMenu hidden={learningTechCollapsible}>
          <SideBarLink to="/manage-bookings" title="Manage Bookings" isCollapsed={isCollapsed} />
          <SideBarLink to="/manage-companies" title="Manage Companies" isCollapsed={isCollapsed} />
          <SideBarLink to="/projects" title="Project Submissions" isCollapsed={isCollapsed} />
          <SideBarLink to="/almax-submissions" title="Assignment Submissions" isCollapsed={isCollapsed} />
          <SideBarLink to="/manage-classroom" title="Manage Classroom" isCollapsed={isCollapsed} />
          <SideBarLink to="/manage-abcat-extension" title="Manage ABCAT" isCollapsed={isCollapsed} />
          <SideBarLink to="/progress-call" title="Progress Call" isCollapsed={isCollapsed} />
          <SideBarLink to="/cohorts" title="Cohorts" isCollapsed={isCollapsed} />
          <SideBarLink to="/support-settings" title="Support Settings" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* Mentorship Controller */}
        <MenuItem
          icon={<SessionContollerIcon iconStyle="h-4 w-4" />}
          label="Mentorship Controller"
          onClick={() => handleMenuClick(setSessionCollapsible, sessionCollapsible)}
          showChevron={!isCollapsed}
          chevronCollapsed={sessionCollapsible}
          isActive={isSectionActive(['/session-visibility', '/session-builder', '/list-rating-templates', '/question-builder', '/session-settings'])}
        />
        <SubMenu hidden={sessionCollapsible}>
          <SideBarLink to="/session-visibility" title="Session Visibility" isCollapsed={isCollapsed} />
          <SideBarLink to="/session-builder" title="Session Builder" isCollapsed={isCollapsed} />
          <SideBarLink to="/list-rating-templates" title="Rating Template" isCollapsed={isCollapsed} />
          <SideBarLink to="/question-builder" title="Question Builder" isCollapsed={isCollapsed} />
          <SideBarLink to="/session-settings" title="Session Settings" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* AlmaX */}
        <MenuItem
          icon={<UsersIcon iconStyle="w-5 h-5" />}
          label="AlmaX"
          moduleAccess={hasModuleAccess(MODULE_NAMES.ALMAX_MODULE)}
          onClick={() => handleMenuClick(setAlmaXCollapsible, almaXCollapsible)}
          showChevron={!isCollapsed}
          chevronCollapsed={almaXCollapsible}
          isActive={isSectionActive(['/ab-cat-report', '/almax-students', '/almax-cohorts'])}
        />
        <SubMenu hidden={almaXCollapsible}>
          <SideBarLink to="/ab-cat-report" title="AB-CAT Report" isCollapsed={isCollapsed} />
          <SideBarLink to="/almax-students" title="All AlmaX Students" isCollapsed={isCollapsed} />
          <SideBarLink to="/almax-cohorts" title="AlmaX Cohorts" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* AlmaX App */}
        <MenuItem
          icon={<DashboardIcon iconStyle="h-4 w-4" />}
          label="AlmaX App"
          moduleAccess={hasModuleAccess('jobDashboard')}
          onClick={() => handleMenuClick(setJobDashboardV2Collapse, jobDashboardV2Collapse)}
          showChevron={!isCollapsed}
          chevronCollapsed={jobDashboardV2Collapse}
          isActive={isSectionActive(['/job-dashboardv2'])}
        />
        <SubMenu hidden={jobDashboardV2Collapse}>
          <SideBarLink to="/job-dashboardv2/job-groups" title="Job Groups" isCollapsed={isCollapsed} />
          <SideBarLink to="/job-dashboardv2/chat/home" title="Chat" isCollapsed={isCollapsed} />
          <SideBarLink to="/job-dashboardv2/interview-schedule" title="Interview Scheduled" isCollapsed={isCollapsed} />
          <SideBarLink to="/job-dashboardv2/activities" title="Activities" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* Job Openings */}
        <MenuItem
          icon={<ManageBookingsIcon iconStyle="w-5 h-5" />}
          label="Job Openings"
          moduleAccess={hasModuleAccess('jobScraping')}
          onClick={() => handleMenuClick(setScrappedJobsCollapsible, scrapedJobsCollapsible)}
          showChevron={!isCollapsed}
          chevronCollapsed={scrapedJobsCollapsible}
          isActive={isSectionActive(['/job-scraping'])}
        />
        <SubMenu hidden={scrapedJobsCollapsible}>
          <SideBarLink to="/job-scraping" title="Job Openings" isCollapsed={isCollapsed} />
        </SubMenu>

        {/* RBAC */}
        <SideBarLink to="/rbac" title="RBAC" isCollapsed={isCollapsed}>
          {(match) => (
            <AdminIcon iconStyle={`h-5 w-5 flex-shrink-0 ${match ? 'text-blue-500' : ''}`} />
          )}
        </SideBarLink>

      </div>

      {/* Logout Button at Bottom */}
      <div style={{ padding: '0 6px', marginTop: 'auto', paddingTop: 8 }}>
        <div
          onClick={logout}
          style={{
            padding: isCollapsed ? '8px 0' : '8px 12px',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            borderRadius: '6px',
            marginBottom: '8px',
            backgroundColor: 'transparent',
            transition: 'background-color 150ms ease',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          className="hover:bg-red-50"
          title={isCollapsed ? 'Logout' : ''}
        >
          <span style={{ flexShrink: 0, minWidth: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LogoutIcon iconStyle="w-5" color="#EF4444" />
          </span>
          <span
            style={{
              margin: isCollapsed ? '0' : 'auto 0 auto 12px',
              flexGrow: isCollapsed ? 0 : 1,
              whiteSpace: 'nowrap',
              opacity: isCollapsed ? 0 : 1,
              width: isCollapsed ? 0 : 'auto',
              transition: 'opacity 200ms, width 200ms, margin 200ms',
              overflow: 'hidden',
              fontSize: '13px',
              fontWeight: 500,
              color: '#EF4444',
              lineHeight: 1.4,
            }}
          >
            Logout
          </span>
        </div>
      </div>
    </div>
  );
}

function SideBarLink({ to, children, title, number, isCollapsed, icon, isNested = false, showTick = false, exact = false }) {
  const allowedPaths = useSelector((state) => state.userReducer.allowedPaths);
  const match = useRouteMatch({ path: to, exact });

  if (
    !allowedPaths.includes('/') &&
    !allowedPaths.includes(to) &&
    !allowedPaths.includes('/' + to.split('/')[1])
  )
    return null;

  const isActive = !!match;

  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div
        title={isCollapsed ? title : ''}
        style={{
          position: 'relative',
          padding: isCollapsed ? '8px 0' : isNested ? '7px 12px 7px 20px' : '9px 12px 9px 20px',
          margin: '1px 0',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          borderRadius: '6px',
          backgroundColor: isActive ? 'var(--sidebar-active-bg, #EEF2FF)' : 'transparent',
          transition: 'background-color 150ms ease',
          minHeight: isNested ? 'unset' : 'unset',
        }}
        className={`flex items-center cursor-pointer transition-all duration-150 hover:bg-[var(--sidebar-hover-bg, #F7F7F9)] ${
          isActive ? 'font-semibold' : ''
        }`}
      >
        {/* Horizontal tick mark */}
        {!isCollapsed && showTick && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: isNested ? '12px' : '14px',
              width: isNested ? '10px' : '12px',
              height: '1px',
              backgroundColor: 'var(--border-light, #E5E7EB)',
            }}
          />
        )}
        {icon && (
          <span style={{ flexShrink: 0 }}>
            {typeof icon === 'function' ? icon(match) : icon}
          </span>
        )}
        {children && children(match)}
        <span
          style={{
            marginLeft: isCollapsed ? '0' : (icon || children ? '12px' : '0'),
            whiteSpace: 'nowrap',
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : 'auto',
            transition: 'opacity 200ms, width 200ms, margin 200ms',
            overflow: 'hidden',
            fontSize: isNested ? '12px' : '12.5px',
            fontWeight: isActive ? 600 : isNested ? 400 : 500,
            color: isActive
              ? 'var(--sidebar-active, #4F46E5)'
              : isNested ? '#9CA3AF' : '#6B7280',
            lineHeight: 1.4,
          }}
        >
          {title}
        </span>
      </div>
    </Link>
  );
}
